import axios from 'axios';

const getApiBaseUrl = () => {
  const isProduction = process.env.NODE_ENV === 'production';

  if (isProduction) {
    return process.env.REACT_APP_API_BASE_URL || 'https://api.gethomecrowd.com/api';
  }

  return process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';
};

const API_BASE_URL = getApiBaseUrl();

class ApiService {
  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
              const refreshToken = localStorage.getItem('refresh_token');
              if (refreshToken) {
                console.log('Attempting to refresh token...');
                const response = await axios.post(`${API_BASE_URL}/token/refresh/`, {
                  refresh: refreshToken,
                });

              const { access } = response.data;
              localStorage.setItem('access_token', access);

              // Update the authorization header for the retry
              originalRequest.headers.Authorization = `Bearer ${access}`;
              console.log('Token refreshed successfully, retrying request...');
              return this.api(originalRequest);
            } else {
              console.log('No refresh token found, redirecting to login');
              localStorage.removeItem('access_token');
              localStorage.removeItem('refresh_token');
              localStorage.removeItem('user');
              window.location.href = '/login';
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
            window.location.href = '/login';
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Auth methods
  async login(email, password) {
    const response = await this.api.post('/auth/login/', { email, password, admin_portal: true });
    const { user, tokens } = response.data;

    localStorage.setItem('access_token', tokens.access);
    localStorage.setItem('refresh_token', tokens.refresh);
    localStorage.setItem('user', JSON.stringify(user));

    return { user, tokens };
  }

  async register(userData) {
    const response = await this.api.post('/auth/register/', userData);
    const { user, tokens } = response.data;

    localStorage.setItem('access_token', tokens.access);
    localStorage.setItem('refresh_token', tokens.refresh);
    localStorage.setItem('user', JSON.stringify(user));

    return { user, tokens };
  }

  async verifyEmail(token) {
    const response = await this.api.post('/users/verify-email/', { token });
    return response.data;
  }

  async getPublicProfile(fanId) {
    const response = await axios.get(
      `${API_BASE_URL}/users/public-profile/${encodeURIComponent(String(fanId || '').trim())}/`
    );
    return response.data;
  }

  async getPublicSchoolBranding(schoolId) {
    const response = await axios.get(
      `${API_BASE_URL}/school/merchants-page-config/${encodeURIComponent(String(schoolId || '').trim())}/`
    );
    return response.data;
  }

  async resetPassword({ uid, token, new_password }) {
    const response = await this.api.post('/auth/reset-password/', {
      uid,
      token,
      new_password,
    });
    return response.data;
  }

  logout() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  }

  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  isAuthenticated() {
    return !!localStorage.getItem('access_token');
  }

  async fetchCurrentUser() {
    try {
      const response = await this.api.get('/auth/me/');
      const user = response.data;

      // Update localStorage with fresh user data
      localStorage.setItem('user', JSON.stringify(user));

      return user;
    } catch (error) {
      // If fetching fails, fall back to localStorage
      return this.getCurrentUser();
    }
  }

  // API methods
  async getTransactions() {
    const response = await this.api.get('/transactions/');
    return response.data;
  }

  async createTransaction(transactionData) {
    const response = await this.api.post('/transactions/', transactionData);
    return response.data;
  }

  async getRewards() {
    const response = await this.api.get('/rewards/rewards/');
    return response.data;
  }

  async getLeaderboardRewards() {
    const response = await this.api.get('/rewards/leaderboard-rewards/');
    return response.data;
  }

  async createLeaderboardReward(payload, images = []) {
    const formData = new FormData();
    Object.entries(payload || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      formData.append(key, value);
    });
    (images || []).forEach(file => {
      formData.append('images', file);
    });
    const response = await this.api.post('/rewards/leaderboard-rewards/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async updateLeaderboardReward(rewardId, payload, images = [], removeImageIds = []) {
    const formData = new FormData();
    Object.entries(payload || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || (value === '' && key !== 'cover_image')) return;
      formData.append(key, value);
    });
    (images || []).forEach(file => {
      formData.append('images', file);
    });
    // Uploads append to the existing gallery; removals have to be explicit.
    (removeImageIds || []).forEach(id => {
      formData.append('remove_image_ids', id);
    });
    const response = await this.api.patch(`/rewards/leaderboard-rewards/${rewardId}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async getFeaturedRewards() {
    const response = await this.api.get('/rewards/rewards/featured/');
    return response.data;
  }

  // Read-only ladder for the staff preview, scoped to the caller's own school.
  async getStaffFirstRewards() {
    const response = await this.api.get('/rewards/first-rewards/staff/');
    return response.data;
  }

  async getFirstRewardRedemptions() {
    const response = await this.api.get('/rewards/first-reward-redemptions/');
    return response.data;
  }

  async updateFirstRewardRedemptionStatus(redemptionId, status) {
    const response = await this.api.patch(
      `/rewards/first-reward-redemptions/${redemptionId}/status/`,
      { status }
    );
    return response.data;
  }

  async getStaffFulfillment() {
    const response = await this.api.get('/rewards/staff/fulfillment/');
    return response.data;
  }

  async updateStaffFulfillmentStatus(source, itemId, status) {
    const response = await this.api.patch(
      `/rewards/staff/fulfillment/${source}/${itemId}/status/`,
      { status }
    );
    return response.data;
  }

  async updateStaffFulfillmentNotes(source, itemId, notes) {
    const response = await this.api.patch(
      `/rewards/staff/fulfillment/${source}/${itemId}/notes/`,
      { notes }
    );
    return response.data;
  }

  async getRedemptions() {
    const response = await this.api.get('/rewards/redemptions/');
    return response.data;
  }

  async createRedemption(rewardId, pointsRequired) {
    const response = await this.api.post('/rewards/redemptions/', {
      reward: rewardId,
      points_spent: pointsRequired,
    });
    return response.data;
  }

  async createStripeRewardCheckoutSession(rewardId) {
    const response = await this.api.post('/rewards/stripe/create-checkout-session/', {
      reward_id: rewardId,
    });
    return response.data;
  }

  async createReward(rewardData, images) {
    // Create FormData for multipart request
    const formData = new FormData();

    // Add reward data
    formData.append('title', rewardData.name);
    formData.append('description', rewardData.description);
    formData.append('redemption_type', rewardData.redemptionType);
    const pointsCost =
      rewardData.redemptionType === 'auction' || rewardData.redemptionType === 'card'
        ? '0'
        : rewardData.points;
    formData.append('points_cost', pointsCost);
    formData.append('reward_type', rewardData.category.toLowerCase().replace(/\s+/g, '_'));
    formData.append('is_featured', rewardData.isFeatured ? 'true' : 'false');
    if (rewardData.category === 'Gift Card') {
      formData.append('gift_card_codes', JSON.stringify(rewardData.giftCardCodes || []));
    }

    if (
      rewardData.redemptionType === 'card' &&
      rewardData.cashPrice &&
      String(rewardData.cashPrice).trim() !== ''
    ) {
      const cents = Math.round(parseFloat(rewardData.cashPrice) * 100);
      if (!Number.isNaN(cents) && cents >= 50) {
        formData.append('cash_price_cents', String(cents));
      }
    }

    // Add drawing date if this is a raffle (convert local time to UTC)
    if (rewardData.redemptionType === 'raffle' && rewardData.drawingDate && rewardData.drawingTime) {
      const localDateTime = new Date(`${rewardData.drawingDate}T${rewardData.drawingTime}`);
      formData.append('drawing_date', localDateTime.toISOString());
    }

    if (rewardData.redemptionType === 'raffle' && rewardData.startDate && rewardData.startTime) {
      const startDateTime = new Date(`${rewardData.startDate}T${rewardData.startTime}`);
      formData.append('start_date', startDateTime.toISOString());
    }

    // Add auction fields if this is an auction (convert local time to UTC)
    if (rewardData.redemptionType === 'auction') {
      if (rewardData.startDate && rewardData.startTime) {
        const startDateTime = new Date(`${rewardData.startDate}T${rewardData.startTime}`);
        formData.append('start_date', startDateTime.toISOString());
      }
      if (rewardData.endDate && rewardData.endTime) {
        const endDateTime = new Date(`${rewardData.endDate}T${rewardData.endTime}`);
        formData.append('end_date', endDateTime.toISOString());
      }
      if (rewardData.startingBid) {
        formData.append('starting_bid', parseFloat(rewardData.startingBid));
      }
      if (rewardData.minimumIncrement) {
        formData.append('minimum_increment', parseFloat(rewardData.minimumIncrement));
      }
    }

    // Add images
    images.forEach((image, index) => {
      formData.append(`images`, image);
    });

    const response = await this.api.post('/rewards/rewards/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }



  async updateReward(rewardId, updateData) {
    const response = await this.api.patch(`/rewards/rewards/${rewardId}/`, updateData);
    return response.data;
  }

  async updateRewardWithImages(rewardId, rewardData, images, existingImages = []) {
    const formData = new FormData();

    formData.append('title', rewardData.name);
    formData.append('description', rewardData.description);
    formData.append('redemption_type', rewardData.redemptionType);
    formData.append('points_cost', rewardData.points);
    formData.append('reward_type', rewardData.category.toLowerCase());

    // Include information about existing images to preserve
    if (existingImages && existingImages.length > 0) {
      existingImages.forEach((existingImage, index) => {
        formData.append(`existing_image_ids`, existingImage.id);
      });
    }

    images.forEach((image, index) => {
      formData.append(`images`, image);
    });


    const response = await this.api.patch(`/rewards/rewards/${rewardId}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async removeImagesFromReward(rewardId, imageIds) {
    const response = await this.api.delete(`/rewards/rewards/${rewardId}/remove_images/`, {
      data: { image_ids: imageIds }
    });
    return response.data;
  }

  async addImagesToReward(rewardId, images) {
    const formData = new FormData();

    images.forEach((image, index) => {
      formData.append(`images`, image);
    });

    const response = await this.api.post(`/rewards/rewards/${rewardId}/add_images/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async toggleRewardStatus(rewardId, isActive) {
    const response = await this.api.patch(`/rewards/rewards/${rewardId}/`, {
      is_active: !isActive
    });
    return response.data;
  }

  async deleteReward(rewardId) {
    const response = await this.api.delete(`/rewards/rewards/${rewardId}/`);
    return response.data;
  }

  async getSchools() {
    const all = [];
    let page = 1;

    while (true) {
      const response = await this.api.get('/school/schools/', { params: { page } });
      const data = response.data;

      if (Array.isArray(data)) {
        return data;
      }

      all.push(...(data.results || []));
      if (!data.next) {
        return {
          ...data,
          count: data.count ?? all.length,
          next: null,
          previous: null,
          results: all,
        };
      }
      page += 1;
    }
  }

  async getPublicSchools() {
    const response = await this.api.get('/school/public-schools/');
    return response.data;
  }

  async getOnboardingTimeline(schoolId) {
    const response = await axios.get(`${API_BASE_URL}/school/onboarding/${schoolId}/`);
    return response.data;
  }

  async updateOnboardingTimeline(schoolId, timeline) {
    const response = await this.api.patch(`/school/schools/${schoolId}/onboarding/`, timeline);
    return response.data;
  }

  async toggleOnboardingAction(schoolId, key, checked) {
    const response = await this.api.patch(`/school/schools/${schoolId}/onboarding_checked/`, { key, checked });
    return response.data;
  }

  async getRewardSelections(schoolId) {
    const response = await axios.get(`${API_BASE_URL}/school/reward-selections/${schoolId}/`);
    return response.data;
  }

  async saveRewardSelections(schoolId, data) {
    const response = await this.api.patch(`/school/schools/${schoolId}/reward_selections/`, data);
    return response.data;
  }

  async getMerchantsPageConfig(schoolId) {
    const response = await this.api.get(`/school/merchants-page-config/${schoolId}/`);
    return response.data;
  }

  async submitMerchantInterest(schoolId, data) {
    const response = await this.api.post(`/school/merchant-interest/${schoolId}/`, data);
    return response.data;
  }

  async updateSchool(schoolId, data) {
    const response = await this.api.patch(`/school/schools/${schoolId}/`, data);
    return response.data;
  }

  async uploadSchoolImages(schoolId, logoFile, bannerFile) {
    const formData = new FormData();

    if (logoFile) {
      formData.append('logo', logoFile);
    }
    if (bannerFile) {
      formData.append('banner', bannerFile);
    }

    const response = await this.api.post(`/school/schools/${schoolId}/upload_images/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getSchoolLeaderboardConfig(schoolId) {
    const response = await this.api.get(`/school/schools/${schoolId}/leaderboard-config/`);
    return response.data;
  }

  async patchSchoolLeaderboardConfig(schoolId, data) {
    const response = await this.api.patch(`/school/schools/${schoolId}/leaderboard-config/`, data);
    return response.data;
  }

  async getSchoolFirstRewards(schoolId) {
    const response = await this.api.get(`/school/schools/${schoolId}/first-rewards/`);
    return response.data;
  }

  async putSchoolFirstRewards(schoolId, rewards) {
    const response = await this.api.put(`/school/schools/${schoolId}/first-rewards/`, rewards);
    return response.data;
  }

  async uploadFirstRewardImage(schoolId, file) {
    const formData = new FormData();
    formData.append('image', file);

    const response = await this.api.post(
      `/school/schools/${schoolId}/first-rewards/upload-image/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  async getSchoolAchievementBadges(schoolId) {
    const response = await this.api.get(`/school/schools/${schoolId}/achievement-badges/`);
    return response.data;
  }

  async putSchoolAchievementBadges(schoolId, badges) {
    const payload = Array.isArray(badges) ? { slugs: badges } : badges;
    const response = await this.api.put(
      `/school/schools/${schoolId}/achievement-badges/`,
      payload
    );
    return response.data;
  }

  async uploadSchoolAchievementBadgeImage(schoolId, file) {
    const formData = new FormData();
    formData.append('image', file);

    const response = await this.api.post(
      `/school/schools/${schoolId}/achievement-badges/upload-image/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  async getSchoolLeaderboardLastWinner(schoolId) {
    const response = await this.api.get(`/school/schools/${schoolId}/leaderboard-last-winner/`);
    return response.data;
  }

  async getSchoolEmbedApiKey(schoolId) {
    const response = await this.api.get(`/school/schools/${schoolId}/embed-api-key/`);
    return response.data;
  }

  async createSchoolEmbedApiKey(schoolId) {
    const response = await this.api.post(`/school/schools/${schoolId}/embed-api-key/`);
    return response.data;
  }

  async rotateSchoolEmbedApiKey(schoolId) {
    const response = await this.api.post(`/school/schools/${schoolId}/embed-api-key/rotate/`);
    return response.data;
  }

  async getSchoolEmbedAppearance(schoolId) {
    const response = await this.api.get(`/school/schools/${schoolId}/embed-appearance/`);
    return response.data;
  }

  async updateSchoolEmbedAppearance(schoolId, colors) {
    const response = await this.api.patch(`/school/schools/${schoolId}/embed-appearance/`, colors);
    return response.data;
  }

  async uploadSchoolEmbedHeaderLogo(schoolId, logoFile) {
    const formData = new FormData();
    formData.append('logo', logoFile);

    const response = await this.api.post(
      `/school/schools/${schoolId}/embed-appearance/header-logo/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  async uploadSchoolEmbedWelcomeScreenImage(schoolId, imageFile) {
    const formData = new FormData();
    formData.append('background', imageFile);

    const response = await this.api.post(
      `/school/schools/${schoolId}/embed-appearance/welcome-screen-image/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  async uploadSchoolEmbedLoginBackground(schoolId, backgroundFile) {
    const formData = new FormData();
    formData.append('background', backgroundFile);

    const response = await this.api.post(
      `/school/schools/${schoolId}/embed-appearance/login-background/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  async uploadSchoolEmbedEmailSelectionBackground(schoolId, backgroundFile) {
    const formData = new FormData();
    formData.append('background', backgroundFile);

    const response = await this.api.post(
      `/school/schools/${schoolId}/embed-appearance/email-selection-background/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  async uploadOfferImages(smallLogoFile, largeLogoFile) {
    const formData = new FormData();

    if (smallLogoFile) {
      formData.append('small_logo', smallLogoFile);
    }
    if (largeLogoFile) {
      formData.append('large_logo', largeLogoFile);
    }

    const response = await this.api.post('/merchant/upload-offer-images/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getMerchants(schoolId = null) {
    const params = schoolId ? { school: schoolId } : {};
    const response = await this.api.get('/merchant/merchants/', { params });
    return response.data;
  }

  // --- Data Portal (superuser: Management -> Data Portal) ---

  async getDataPortal(schoolId) {
    const response = await this.api.get(`/school/data-portal/${schoolId}/`);
    return response.data;
  }

  async uploadDataPortalFile(schoolId, datasetKey, file, notes = '') {
    const formData = new FormData();
    formData.append('dataset_key', datasetKey);
    formData.append('file', file);
    if (notes) formData.append('notes', notes);
    const response = await this.api.post(`/school/data-portal/${schoolId}/uploads/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteDataPortalUpload(schoolId, uploadId) {
    const response = await this.api.delete(`/school/data-portal/${schoolId}/uploads/${uploadId}/`);
    return response.data;
  }

  // --- School Pay (superuser: Financials -> School Pay) ---

  async getSchoolPayOverview() {
    const response = await this.api.get('/school/school-pay/overview/');
    return response.data;
  }

  async markSchoolPaySent(payload) {
    const response = await this.api.post('/school/school-pay/mark-sent/', payload);
    return response.data;
  }

  async unmarkSchoolPaySent(payload) {
    const response = await this.api.post('/school/school-pay/unmark-sent/', payload);
    return response.data;
  }

  // --- Sport schedules (superuser: School Management -> Schedule) ---

  async getSportSchedules(schoolId) {
    const response = await this.api.get('/school/sport-schedules/', {
      params: { school: schoolId },
    });
    const data = response.data;
    return Array.isArray(data) ? data : data?.results || [];
  }

  async createSportSchedule(schoolId, sport) {
    const response = await this.api.post('/school/sport-schedules/', {
      school: schoolId,
      sport,
    });
    return response.data;
  }

  async deleteSportSchedule(scheduleId) {
    await this.api.delete(`/school/sport-schedules/${scheduleId}/`);
  }

  async getScheduleEvents(scheduleId) {
    const response = await this.api.get('/school/schedule-events/', {
      params: { schedule: scheduleId },
    });
    const data = response.data;
    return Array.isArray(data) ? data : data?.results || [];
  }

  // `logoFile` is only sent for opponents with no matching school on record;
  // when opponent_school is set the backend reads the logo from that school.
  async saveScheduleEvent(payload, logoFile = null, eventId = null) {
    const url = eventId
      ? `/school/schedule-events/${eventId}/`
      : '/school/schedule-events/';
    const send = eventId ? this.api.patch.bind(this.api) : this.api.post.bind(this.api);

    if (!logoFile) {
      const response = await send(url, payload);
      return response.data;
    }

    const formData = new FormData();
    Object.entries(payload || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      formData.append(key, value);
    });
    formData.append('opponent_logo', logoFile);
    const response = await send(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async getScheduleEventPhotos(eventId) {
    const response = await this.api.get(`/school/schedule-events/${eventId}/photos/`);
    return response.data;
  }

  async markScheduleEventPhotoWinner(eventId, photoId) {
    const response = await this.api.post(
      `/school/schedule-events/${eventId}/photos/${photoId}/winner/`
    );
    return response.data;
  }

  async getSchoolSeasonTickets(schoolId) {
    const response = await this.api.get(`/school/schools/${schoolId}/season-tickets/`);
    return response.data;
  }

  async putSchoolSeasonTickets(schoolId, payload) {
    const response = await this.api.put(
      `/school/schools/${schoolId}/season-tickets/`,
      payload
    );
    return response.data;
  }

  async deleteScheduleEvent(eventId) {
    await this.api.delete(`/school/schedule-events/${eventId}/`);
  }

  async getFeaturedOffers(schoolId = null, extraParams = {}) {
    const params = { ...extraParams };
    if (schoolId) params.school = schoolId;
    const response = await this.api.get('/merchant/featured-offers/', { params });
    return response.data;
  }

  async createFeaturedOffer(data) {
    const response = await this.api.post('/merchant/featured-offers/', data);
    return response.data;
  }

  async updateFeaturedOffer(offerId, data) {
    const response = await this.api.patch(`/merchant/featured-offers/${offerId}/`, data);
    return response.data;
  }

  async deleteFeaturedOffer(offerId) {
    const response = await this.api.delete(`/merchant/featured-offers/${offerId}/`);
    return response.data;
  }

  async getAdminWildfireMerchants() {
    const response = await this.api.get('/admin/wildfire-merchants/');
    return response.data;
  }

  async getWildfireOfferOrder({ schoolId }) {
    const response = await this.api.get('/merchant/wildfire-offer-order/', { params: { school_id: schoolId } });
    return response.data;
  }

  async saveWildfireOfferOrder({ schoolId, entries }) {
    const response = await this.api.put('/merchant/wildfire-offer-order/', { school_id: schoolId, entries });
    return response.data;
  }

  async getAdminOliveMerchants({ forceRefresh = false } = {}) {
    const params = forceRefresh ? { force_refresh: 1 } : {};
    const response = await this.api.get('/admin/olive-merchants/', { params });
    return response.data;
  }

  async getAdminOliveNearby({ lat, lng, limit = 100, radiusKm = 100 } = {}) {
    const response = await this.api.get('/admin/olive-nearby/', {
      params: { lat, lng, limit, radius_km: radiusKm },
    });
    return response.data;
  }

  async lookupAdminMerchant({ source, merchantId }) {
    const response = await this.api.get('/admin/merchant-lookup/', {
      params: { source, merchant_id: merchantId },
    });
    return response.data;
  }

  async getAdminMerchantPointMultipliers() {
    const response = await this.api.get('/admin/merchant-point-multipliers/');
    return response.data;
  }

  async upsertAdminMerchantPointMultiplier(payload) {
    const response = await this.api.post('/admin/merchant-point-multipliers/', payload);
    return response.data;
  }

  async patchAdminMerchantPointMultiplier(multiplierId, payload) {
    const response = await this.api.patch(`/admin/merchant-point-multipliers/${multiplierId}/`, payload);
    return response.data;
  }

  async deleteAdminMerchantPointMultiplier(multiplierId) {
    const response = await this.api.delete(`/admin/merchant-point-multipliers/${multiplierId}/`);
    return response.data;
  }

  async getAdminMerchantPointMultipliersHistory(params = {}) {
    const response = await this.api.get('/admin/merchant-point-multipliers/history/', { params });
    return response.data;
  }

  async getAdminMerchantPointMultiplierSchedules(multiplierId) {
    const response = await this.api.get(`/admin/merchant-point-multipliers/${multiplierId}/schedules/`);
    return response.data;
  }

  async createAdminMerchantPointMultiplierSchedule(multiplierId, payload) {
    const response = await this.api.post(`/admin/merchant-point-multipliers/${multiplierId}/schedules/`, payload);
    return response.data;
  }

  async deleteAdminMerchantPointMultiplierSchedule(multiplierId, scheduleId) {
    await this.api.delete(`/admin/merchant-point-multipliers/${multiplierId}/schedules/${scheduleId}/`);
  }

  async getUsers() {
    const response = await this.api.get('/users/users/');
    return response.data;
  }

  async getUserSchools() {
    const response = await this.api.get('/users/user-schools/');
    return response.data;
  }

  async getAllUsersStaff() {
    const response = await this.api.get('/users/staff/all-users/');
    return response.data;
  }

  async getStaffReceipts(params = {}) {
    const response = await this.api.get('/users/staff/receipts/', { params });
    return response.data;
  }

  async getStaffReceiptReviewSettings() {
    const response = await this.api.get('/users/staff/receipts/settings/');
    return response.data;
  }

  async updateStaffReceiptReviewSettings(payload) {
    const response = await this.api.patch('/users/staff/receipts/settings/', payload);
    return response.data;
  }

  async approveStaffReceipt(receiptId, payload = {}) {
    const response = await this.api.post(`/users/staff/receipts/${receiptId}/approve/`, payload);
    return response.data;
  }

  async declineStaffReceipt(receiptId, payload = {}) {
    const response = await this.api.post(`/users/staff/receipts/${receiptId}/decline/`, payload);
    return response.data;
  }

  async getAllUsersSuperuser(params = {}) {
    const response = await this.api.get('/users/superuser/all-users/', { params });
    return response.data;
  }

  async getRewardExclusionsSuperuser(params = {}) {
    const response = await this.api.get('/users/superuser/reward-exclusions/', { params });
    return response.data;
  }

  async addRewardExclusionSuperuser(userId) {
    const response = await this.api.post('/users/superuser/reward-exclusions/', { user_id: userId });
    return response.data;
  }

  async removeRewardExclusionSuperuser(userId) {
    const response = await this.api.delete(`/users/superuser/reward-exclusions/${userId}/`);
    return response.data;
  }

  async getCheckInLocationsSuperuser() {
    const response = await this.api.get('/users/superuser/check-in-locations/');
    return response.data;
  }

  async createCheckInLocationSuperuser(payload) {
    const response = await this.api.post('/users/superuser/check-in-locations/', payload);
    return response.data;
  }

  async updateCheckInLocationSuperuser(locationId, payload) {
    const response = await this.api.patch(
      `/users/superuser/check-in-locations/${locationId}/`,
      payload
    );
    return response.data;
  }

  async deleteCheckInLocationSuperuser(locationId) {
    const response = await this.api.delete(`/users/superuser/check-in-locations/${locationId}/`);
    return response.data;
  }

  async getCheckInsSuperuser(params = {}) {
    const response = await this.api.get('/users/superuser/check-ins/', { params });
    return response.data;
  }

  async getTestFreeEntriesSuperuser() {
    const response = await this.api.get('/rewards/superuser/test-free-entries/');
    return response.data;
  }

  async saveTestFreeEntriesSuperuser(payload) {
    const response = await this.api.put('/rewards/superuser/test-free-entries/', payload);
    return response.data;
  }

  async sendTestFreeEntriesSuperuser(payload) {
    const response = await this.api.post('/rewards/superuser/test-free-entries/send/', payload);
    return response.data;
  }

  async getManagedLinksSuperuser() {
    const response = await this.api.get('/users/superuser/managed-links/');
    return response.data;
  }

  async updateManagedLinksSuperuser(payload) {
    const response = await this.api.patch('/users/superuser/managed-links/', payload);
    return response.data;
  }

  async getEmbeddingDocs() {
    const response = await this.api.get('/users/managed-docs/embedding/');
    return response.data;
  }

  async getEmbeddingDocsSuperuser() {
    const response = await this.api.get('/users/superuser/managed-docs/embedding/');
    return response.data;
  }

  async updateEmbeddingDocsSuperuser(document) {
    const response = await this.api.patch('/users/superuser/managed-docs/embedding/', { document });
    return response.data;
  }

  async createUserSuperuser(userData) {
    const response = await this.api.post('/users/superuser/create-user/', userData);
    return response.data;
  }

  async generateAdminLinkSuperuser(payload) {
    const response = await this.api.post('/users/superuser/generate-admin-link/', payload);
    return response.data;
  }

  async sendStaffAccountCreatedEmailSuperuser(email) {
    const response = await this.api.post('/users/superuser/send-staff-account-created-email/', { email });
    return response.data;
  }

  // Gives an account that already exists staff access to a school. Separate
  // from generateAdminLinkSuperuser on purpose: that one only ever reports
  // existing accounts, this is the deliberate act of changing one.
  async promoteStaffUsersSuperuser(payload) {
    const response = await this.api.post('/users/superuser/promote-staff-users/', payload);
    return response.data;
  }

  // The promoted-account counterpart of sendStaffAccountCreatedEmailSuperuser.
  // It states no password, because a promoted user keeps their own.
  async sendStaffAccessGrantedEmailSuperuser(email) {
    const response = await this.api.post('/users/superuser/send-staff-access-granted-email/', { email });
    return response.data;
  }

  async getCreatedStaffAdminsHistorySuperuser(params = {}) {
    const response = await this.api.get('/users/superuser/created-staff-admins/', { params });
    return response.data;
  }

  async updateUserSuperuser(userId, userData) {
    const response = await this.api.patch(`/users/superuser/users/${userId}/`, userData);
    return response.data;
  }

  async switchActiveSchoolSuperuser(schoolId) {
    const response = await this.api.post('/users/superuser/switch-school/', {
      school_id: schoolId,
    });
    return response.data;
  }

  async deleteUserSuperuser(userId, masterKey) {
    const response = await this.api.post(`/users/superuser/users/${userId}/delete/`, {
      master_key: masterKey,
    });
    return response.data;
  }

  async createEmbedImpersonationSuperuser(userId, payload = {}) {
    const response = await this.api.post(`/users/superuser/users/${userId}/impersonate-embed/`, payload);
    return response.data;
  }

  async getSuperuserReceipts(params = {}) {
    const response = await this.api.get('/users/superuser/receipts/', { params });
    return response.data;
  }

  async getSuperuserReceiptReviewSettings() {
    const response = await this.api.get('/users/superuser/receipts/settings/');
    return response.data;
  }

  async updateSuperuserReceiptReviewSettings(payload) {
    const response = await this.api.patch('/users/superuser/receipts/settings/', payload);
    return response.data;
  }

  async approveSuperuserReceipt(receiptId, payload = {}) {
    const response = await this.api.post(`/users/superuser/receipts/${receiptId}/approve/`, payload);
    return response.data;
  }

  async declineSuperuserReceipt(receiptId, payload = {}) {
    const response = await this.api.post(`/users/superuser/receipts/${receiptId}/decline/`, payload);
    return response.data;
  }

  async getBillableMerchants() {
    const response = await this.api.get('/admin/billing/merchants/');
    return response.data;
  }

  async getBillingOverview() {
    const response = await this.api.get('/admin/billing/overview/');
    return response.data;
  }

  async getBillingStatus(params) {
    const response = await this.api.get('/admin/billing/status/', { params });
    return response.data;
  }

  async chargeBillingInvoice(payload) {
    const response = await this.api.post('/admin/billing/charge/', payload);
    return response.data;
  }

  async saveBillingProfile(payload) {
    const response = await this.api.post('/admin/billing/profile/', payload);
    return response.data;
  }

  async createBillingOnboardingLink(payload) {
    const response = await this.api.post('/admin/billing/onboarding-link/', payload);
    return response.data;
  }

  async getTransactionEvents(externalTxnId) {
    const response = await this.api.get('/transactions/superuser/transaction-events/', {
      params: { external_txn_id: externalTxnId },
    });
    return response.data;
  }

  async getAllTransactionsSuperuser(params = {}) {
    const response = await this.api.get('/transactions/superuser/all-transactions/', { params });
    return response.data;
  }

  async getSuperuserAnalyticsOverview(params = {}) {
    const response = await this.api.get('/analytics/superuser/overview/', { params });
    return response.data;
  }

  async getSuperuserAnalyticsEvents(params = {}) {
    const response = await this.api.get('/analytics/superuser/events/', { params });
    return response.data;
  }

  async getStaffAnalyticsEvents(params = {}) {
    const response = await this.api.get('/analytics/staff/events/', { params });
    return response.data;
  }

  async getStaffAnalyticsOverview(params = {}) {
    const response = await this.api.get('/analytics/staff/overview/', { params });
    return response.data;
  }

  async getStaffAnalyticsLogins(params = {}) {
    const response = await this.api.get('/analytics/staff/logins/', { params });
    return response.data;
  }

  async getStaffAnalyticsMetrics(params = {}) {
    const response = await this.api.get('/analytics/staff/metrics/', { params });
    return response.data;
  }

  async getStaffAnalyticsMetricSeries(metricKey, params = {}) {
    const response = await this.api.get(`/analytics/staff/metrics/${metricKey}/series/`, { params });
    return response.data;
  }

  async getStaffRewardAnalytics(params = {}) {
    const response = await this.api.get('/analytics/staff/rewards/', { params });
    return response.data;
  }

  async getStaffRewardAnalyticsDetail(rewardId, params = {}) {
    const response = await this.api.get(`/analytics/staff/rewards/${rewardId}/`, { params });
    return response.data;
  }

  async getSuperuserAnalyticsLogins(params = {}) {
    const response = await this.api.get('/analytics/superuser/logins/', { params });
    return response.data;
  }

  async getSuperuserUsersProgress(params = {}) {
    const response = await this.api.get('/analytics/superuser/users-progress/', { params });
    return response.data;
  }

  async getSuperuserRewardAnalytics(params = {}) {
    const response = await this.api.get('/analytics/superuser/rewards/', { params });
    return response.data;
  }

  async getSuperuserRewardAnalyticsDetail(rewardId, params = {}) {
    const response = await this.api.get(`/analytics/superuser/rewards/${rewardId}/`, { params });
    return response.data;
  }

  async getPaymentCards() {
    const response = await this.api.get('/payment-cards/');
    return response.data;
  }

  async createPaymentCard(cardData) {
    const response = await this.api.post('/payment-cards/', cardData);
    return response.data;
  }

  async getUserPointsSummary(userId) {
    const response = await this.api.get(`/users/users/${userId}/points_summary/`);
    return response.data;
  }

  async createOliveMember() {
    const response = await this.api.post('/olive/createMember');
    return response.data;
  }

  async addCard(token) {
    const response = await this.api.post('/olive/addCard', { token });
    return response.data;
  }

  async listOliveCards() {
    const response = await this.api.get('/olive/listCards');
    return response.data;
  }

  async getTokenizationFields() {
    const response = await this.api.post('/olive/getTokenizationFields');
    return response.data;
  }

  async deactivateOliveCard(cardId) {
    const response = await this.api.post('/olive/deactivateCard', { cardId });
    return response.data;
  }

  async listCards() {
    // Get current user to filter cards
    const currentUser = this.getCurrentUser();

    if (!currentUser?.id) {
      throw new Error('User not authenticated');
    }

    // Try to get user-specific cards first
    try {
      const response = await this.api.get(`/users/users/${currentUser.id}/payment-cards/`);
      return { cards: response.data.results || response.data };
    } catch (error) {
      // Fallback to general payment-cards endpoint with user filtering
      if (error.response?.status === 404) {
        const response = await this.api.get('/payment-cards/', {
          params: { user: currentUser.id }
        });
        const allCards = response.data.results || response.data;
        // Filter cards for current user if the API doesn't do it
        const userCards = allCards.filter(card =>
          card.user === currentUser.id ||
          card.user_id === currentUser.id ||
          card.owner === currentUser.id ||
          card.owner_id === currentUser.id
        );
        return { cards: userCards };
      }
      throw error;
    }
  }

  async listOliveTransactions() {
    const response = await this.api.get('/olive/listTransactions');
    return response.data;
  }


  async getOliveOffers(latitude = null, longitude = null) {
    const params = {};
    if (latitude && longitude) {
      params.latitude = latitude;
      params.longitude = longitude;
    }
    const response = await this.api.get('/olive/offers/', { params });
    return response.data;
  }

  async createTestTransaction(amount = 25.99, merchantCategory = '5411') {
    // TODO: Implement when backend endpoint is available
    throw new Error('createTestTransaction endpoint not implemented yet');
  }

  // Raffle methods
  async getRaffles(params = {}) {
    const query = new URLSearchParams(params).toString();
    const response = await this.api.get(`/rewards/raffles/${query ? '?' + query : ''}`);
    return response.data;
  }

  async getRaffle(raffleId) {
    const response = await this.api.get(`/rewards/raffles/${raffleId}/`);
    return response.data;
  }

  async getRaffleEntries(raffleId) {
    const response = await this.api.get(`/rewards/raffles/${raffleId}/entries/`);
    return response.data;
  }

  async drawRaffleWinner(raffleId) {
    const response = await this.api.post(`/rewards/raffles/${raffleId}/draw/`, {});
    return response.data;
  }

  // Auction methods
  async getAuction(auctionId) {
    const response = await this.api.get(`/rewards/auctions/${auctionId}/`);
    return response.data;
  }

  async getAuctionBids(auctionId) {
    const response = await this.api.get(`/rewards/auctions/${auctionId}/bids/`);
    return response.data;
  }

  async placeBid(auctionId, amount) {
    const response = await this.api.post(`/rewards/auctions/${auctionId}/bid/`, {
      amount: amount
    });
    return response.data;
  }

  async getWeeklyPrizes() {
    const response = await this.api.get('/rewards/weekly-prizes/');
    return response.data;
  }

  async createWeeklyPrize(payload) {
    const formData = new FormData();
    Object.entries(payload || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (key === 'cover_image_file') {
        formData.append('cover_image_file', value);
        return;
      }
      formData.append(key, value);
    });
    const response = await this.api.post('/rewards/weekly-prizes/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async updateWeeklyPrize(prizeId, payload) {
    const formData = new FormData();
    Object.entries(payload || {}).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (value === '' && !['cover_image', 'how_to_win'].includes(key)) return;
      if (key === 'cover_image_file') {
        formData.append('cover_image_file', value);
        return;
      }
      formData.append(key, value);
    });
    const response = await this.api.patch(`/rewards/weekly-prizes/${prizeId}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteWeeklyPrize(prizeId) {
    const response = await this.api.delete(`/rewards/weekly-prizes/${prizeId}/`);
    return response.data;
  }

  async getChallenges() {
    const response = await this.api.get('/rewards/challenges/');
    return response.data;
  }

  toChallengePresetBody(payload = {}) {
    if (!payload.icon_file) return payload;
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (key === 'icon_file') {
        formData.append('icon_file', value);
        return;
      }
      if (typeof value === 'boolean') {
        formData.append(key, value ? 'true' : 'false');
        return;
      }
      formData.append(key, value);
    });
    return formData;
  }

  async getChallengePresets() {
    const response = await this.api.get('/rewards/superuser/challenge-presets/');
    return response.data;
  }

  async createChallengePreset(payload) {
    const body = this.toChallengePresetBody(payload);
    const response = await this.api.post('/rewards/superuser/challenge-presets/', body, body instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined);
    return response.data;
  }

  async updateChallengePreset(presetId, payload) {
    const body = this.toChallengePresetBody(payload);
    const response = await this.api.patch(`/rewards/superuser/challenge-presets/${presetId}/`, body, body instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined);
    return response.data;
  }

  async deleteChallengePreset(presetId) {
    const response = await this.api.delete(`/rewards/superuser/challenge-presets/${presetId}/`);
    return response.data;
  }

  async getSchoolChallengeCatalog(schoolId) {
    const response = await this.api.get('/rewards/superuser/school-challenge-catalog/', {
      params: schoolId ? { school_id: schoolId } : { all_schools: true },
    });
    return response.data;
  }

  async saveSchoolChallengeCatalog(schoolId, presetIds) {
    const payload = { preset_ids: presetIds };
    if (schoolId) {
      payload.school_id = schoolId;
    } else {
      payload.all_schools = true;
    }
    const response = await this.api.put('/rewards/superuser/school-challenge-catalog/', payload);
    return response.data;
  }

  async getSchoolChallenges(schoolId) {
    const response = await this.api.get('/rewards/superuser/school-challenges/', {
      params: schoolId ? { school_id: schoolId } : {},
    });
    return response.data;
  }

  async createSchoolChallenge(payload) {
    const response = await this.api.post('/rewards/superuser/school-challenges/', payload);
    return response.data;
  }

  async updateSchoolChallenge(challengeId, payload) {
    const response = await this.api.patch(`/rewards/superuser/school-challenges/${challengeId}/`, payload);
    return response.data;
  }

  async deleteSchoolChallenge(challengeId) {
    const response = await this.api.delete(`/rewards/superuser/school-challenges/${challengeId}/`);
    return response.data;
  }

  async getWeeklyPrizeLeaderboard(prizeId, params = {}) {
    const response = await this.api.get(`/rewards/weekly-prizes/${prizeId}/leaderboard/`, { params });
    return response.data;
  }

  toOverallPrizePayload(payload = {}) {
    const normalizeDate = raw => {
      if (!raw) return raw;
      const value = String(raw);
      return value.includes('T') ? value.split('T')[0] : value;
    };
    const normalizeTime = raw => {
      if (!raw) return raw;
      const value = String(raw);
      if (/^\d{2}:\d{2}/.test(value)) {
        return value.length >= 8 ? value.slice(0, 8) : `${value}:00`;
      }
      return value;
    };
    return {
      ...payload,
      start_date: normalizeDate(payload.start_date),
      end_date: normalizeDate(payload.end_date),
      start_time: normalizeTime(payload.start_time) || '00:00:00',
      end_time: normalizeTime(payload.end_time) || '16:00:00',
    };
  }

  async getOverallPrizes() {
    const response = await this.api.get('/rewards/overall-prizes/');
    return response.data;
  }

  async createOverallPrize(payload) {
    const formData = new FormData();
    Object.entries(this.toOverallPrizePayload(payload) || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (key === 'cover_image_file') {
        formData.append('cover_image_file', value);
        return;
      }
      formData.append(key, value);
    });
    const response = await this.api.post('/rewards/overall-prizes/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async updateOverallPrize(prizeId, payload) {
    const formData = new FormData();
    Object.entries(this.toOverallPrizePayload(payload) || {}).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (value === '' && !['cover_image', 'how_to_win'].includes(key)) return;
      if (key === 'cover_image_file') {
        formData.append('cover_image_file', value);
        return;
      }
      formData.append(key, value);
    });
    const response = await this.api.patch(`/rewards/overall-prizes/${prizeId}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteOverallPrize(prizeId) {
    const response = await this.api.delete(`/rewards/overall-prizes/${prizeId}/`);
    return response.data;
  }

  async getOverallPrizeLeaderboard(prizeId, params = {}) {
    const response = await this.api.get(`/rewards/overall-prizes/${prizeId}/leaderboard/`, { params });
    return response.data;
  }

  async sendMassNotification(title, body, data = {}, schoolIds = [], emails = null) {
    const payload = { title, body, data };
    if (emails && emails.length > 0) {
      payload.emails = emails;
    } else {
      payload.school_ids = schoolIds;
    }
    const response = await this.api.post('/notifications/send-mass/', payload);
    return response.data;
  }

  async getNotificationPreview(payload = {}, params = {}) {
    const response = await this.api.post('/notifications/preview/', payload, { params });
    return response.data;
  }

  async getNotificationHistory(params = {}) {
    const response = await this.api.get('/notifications/history/', { params });
    return response.data;
  }

  async getNotificationDetail(id) {
    const response = await this.api.get(`/notifications/history/${id}/`);
    return response.data;
  }

  async resendNotification(id) {
    const response = await this.api.post(`/notifications/history/${id}/resend/`);
    return response.data;
  }

  async getNotificationHistoryStats(params = {}) {
    const response = await this.api.get('/notifications/history/stats/', { params });
    return response.data;
  }

  async getSchoolAvailabilityEmailQueues() {
    const response = await this.api.get('/school/availability-emails/');
    return response.data;
  }

  async sendSchoolAvailabilityEmails(schoolId) {
    const response = await this.api.post(`/school/availability-emails/${schoolId}/send/`);
    return response.data;
  }

  async addPointsToUser(userId, points) {
    const response = await this.api.post(`/users/superuser/add-points/${userId}/`, {
      points: points,
      description: 'Granted by Homecrowd'
    });
    return response.data;
  }

  // Incentive management methods
  async getIncentiveTriggers() {
    const response = await this.api.get('/rewards/incentives/triggers/');
    return response.data;
  }

  async createIncentiveTrigger(triggerData) {
    const response = await this.api.post('/rewards/incentives/triggers/', triggerData);
    return response.data;
  }

  async updateIncentiveTrigger(triggerId, triggerData) {
    const response = await this.api.put(`/rewards/incentives/triggers/${triggerId}/`, triggerData);
    return response.data;
  }

  async deleteIncentiveTrigger(triggerId) {
    const response = await this.api.delete(`/rewards/incentives/triggers/${triggerId}/`);
    return response.data;
  }

  async getIncentiveCampaigns() {
    const response = await this.api.get('/rewards/incentives/campaigns/');
    return response.data;
  }

  async createIncentiveCampaign(campaignData) {
    const response = await this.api.post('/rewards/incentives/campaigns/', campaignData);
    return response.data;
  }

  async updateIncentiveCampaign(campaignId, campaignData) {
    const response = await this.api.put(`/rewards/incentives/campaigns/${campaignId}/`, campaignData);
    return response.data;
  }

  async deleteIncentiveCampaign(campaignId) {
    const response = await this.api.delete(`/rewards/incentives/campaigns/${campaignId}/`);
    return response.data;
  }

  async recordUserAction(actionData) {
    const response = await this.api.post('/rewards/incentives/actions/', actionData);
    return response.data;
  }

  async getUserIncentiveHistory(params = {}) {
    const response = await this.api.get('/rewards/incentives/history/', { params });
    return response.data;
  }

  async getCampaignAnalytics(campaignId) {
    const response = await this.api.get(`/rewards/incentives/analytics/${campaignId}/`);
    return response.data;
  }

  async getSetupTaskRewardsSuperuser() {
    const response = await this.api.get('/rewards/superuser/setup-task-rewards/');
    return response.data;
  }

  async updateSetupTaskRewardsSuperuser(payload) {
    const response = await this.api.patch('/rewards/superuser/setup-task-rewards/', payload);
    return response.data;
  }

  async getCardDrawConfig(schoolId) {
    const response = await this.api.get('/rewards/superuser/card-draw-config/', {
      params: { school_id: schoolId },
    });
    return response.data;
  }

  async updateCardDrawConfig(schoolId, payload) {
    const response = await this.api.put('/rewards/superuser/card-draw-config/', {
      school_id: schoolId,
      ...payload,
    });
    return response.data;
  }

  // Content management methods
  async getContent() {
    const response = await this.api.get('/content/');
    return response.data;
  }

  async createContent(contentData) {
    try {
      // Get current user to get school info
      const currentUser = this.getCurrentUser();
      console.log('Current user:', currentUser);

      if (!currentUser?.active_school?.id) {
        console.error('User active school issue:', {
          currentUser,
          activeSchool: currentUser?.active_school,
          schoolId: currentUser?.active_school?.id
        });
        throw new Error('User must have an active school to create content');
      }

      // Map frontend fields to backend fields
      const contentPayload = {
        title: contentData.title || '',
        subtitle: contentData.subtitle || '',
        description: contentData.description || '', // description → description
        content_type: this.mapCategoryToContentType(contentData.category), // category → content_type
        status: contentData.status || 'draft', // use provided status or default to draft
        video_url: contentData.videoUrl || '',
        slug: this.generateSlug(contentData.title), // auto-generate slug
        school: currentUser.active_school.id, // get from current user
        featured: contentData.featured || false, // use form value or default to false
      };

      console.log('Sending content payload:', contentPayload);

      // Create the content item first
      const response = await this.api.post('/content/', contentPayload);
      const contentItem = response.data;

      console.log('Content created successfully:', contentItem);

      // Upload thumbnail file if present
      if (contentData.thumbnailFile) {
        console.log('Uploading thumbnail...');
        await this.uploadContentImage(contentItem.id, contentData.thumbnailFile);
      }

      // Upload video file if present
      if (contentData.videoFile) {
        console.log('Uploading video...');
        await this.uploadContentVideo(contentItem.id, contentData.videoFile);
      }

      return contentItem;
    } catch (error) {
      console.error('Error creating content:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    }
  }

  async uploadContentVideo(contentId, videoFile) {
    const formData = new FormData();
    formData.append('video', videoFile);

    const response = await this.api.post(`/content/${contentId}/upload_video/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async uploadContentImage(contentId, imageFile) {
    const formData = new FormData();
    formData.append('images', imageFile);

    const response = await this.api.post(`/content/${contentId}/upload_image/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  mapCategoryToContentType(category) {
    const mapping = {
      'Interview': 'interview',
      'Content': 'content',
      'Announcement': 'announcement',
    };
    return mapping[category] || 'content';
  }

  mapContentTypeToCategory(contentType) {
    const reverseMapping = {
      'interview': 'Interview',
      'content': 'Content',
      'announcement': 'Announcement',
    };
    return reverseMapping[contentType] || 'Content';
  }


  generateSlug(title) {
    if (!title) return '';
    const baseSlug = title.toLowerCase()
      .replace(/[^a-z0-9 ]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 40);

    // Add timestamp to ensure uniqueness
    const timestamp = Date.now().toString().slice(-6);
    return `${baseSlug}-${timestamp}`;
  }

  async updateContentStatus(contentId, status) {
    try {
      const response = await this.api.patch(`/content/${contentId}/`, { status });
      return response.data;
    } catch (error) {
      console.error('Error updating content status:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    }
  }

  async updateContent(contentId, contentData) {
    try {
      // Get current user to get school info
      const currentUser = this.getCurrentUser();
      if (!currentUser?.active_school?.id) {
        throw new Error('User must have an active school to update content');
      }

      // Map frontend fields to backend fields (same as create)
      const contentPayload = {
        title: contentData.title || '',
        subtitle: contentData.subtitle || '',
        description: contentData.description || '', // description → description
        content_type: this.mapCategoryToContentType(contentData.category), // category → content_type
        status: contentData.status || 'draft', // use provided status or default to draft
        video_url: contentData.videoUrl || '',
        slug: this.generateSlug(contentData.title), // auto-generate slug
        school: currentUser.active_school.id, // get from current user
        featured: contentData.featured || false, // use form value or default to false
      };

      console.log('Updating content payload:', contentPayload);

      // Update the content item
      const response = await this.api.patch(`/content/${contentId}/`, contentPayload);
      const contentItem = response.data;

      console.log('Content updated successfully:', contentItem);

      // Handle file uploads separately if present
      if (contentData.thumbnailFile) {
        console.log('Uploading thumbnail...');
        await this.uploadContentImage(contentId, contentData.thumbnailFile);
      }

      if (contentData.videoFile) {
        console.log('Uploading video...');
        await this.uploadContentVideo(contentId, contentData.videoFile);
      }

      return contentItem;
    } catch (error) {
      console.error('Error updating content:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    }
  }

  async deleteContent(contentId) {
    const response = await this.api.delete(`/content/${contentId}/`);
    return response.data;
  }

  async getContentVideos() {
    const response = await this.api.get('/videos/');
    return response.data;
  }

  async deleteContentImage(imageId) {
    const response = await this.api.delete(`/content/images/${imageId}/`);
    return response.data;
  }

  async deleteContentVideo(videoId) {
    const response = await this.api.delete(`/content/videos/${videoId}/`);
    return response.data;
  }

  // Access Development SFTP ingest (Engineering -> Access pulls)
  async getAccessSftpStatus() {
    const response = await this.api.get('/superuser/access-sftp/status/');
    return response.data;
  }

  async toggleAccessSftpSchedule(payload) {
    const response = await this.api.post('/superuser/access-sftp/toggle/', payload);
    return response.data;
  }

  async runAccessSftpPullNow() {
    const response = await this.api.post('/superuser/access-sftp/run-now/');
    return response.data;
  }

  async getAccessSftpRuns(params) {
    const response = await this.api.get('/superuser/access-sftp/runs/', { params });
    return response.data;
  }

  async getAccessSftpRun(runId) {
    const response = await this.api.get(`/superuser/access-sftp/runs/${runId}/`);
    return response.data;
  }

  async getAccessSftpFiles(params) {
    const response = await this.api.get('/superuser/access-sftp/files/', { params });
    return response.data;
  }

  async getAccessSftpFile(fileId) {
    const response = await this.api.get(`/superuser/access-sftp/files/${fileId}/`);
    return response.data;
  }

  async resetAccessSftpFile(fileId) {
    const response = await this.api.post(`/superuser/access-sftp/files/${fileId}/reset/`);
    return response.data;
  }

  // Small files stream back as bytes, so no bearer token ever lands in a URL. Files
  // above the server's proxy cap come back as JSON carrying a short-lived presigned
  // URL instead — the response is still a Blob either way, so the content type is
  // what tells them apart. Saving the JSON as-is would produce a file full of JSON
  // that looks like a successful download.
  async downloadAccessSftpFile(fileId) {
    const response = await this.api.get(
      `/superuser/access-sftp/files/${fileId}/download/`,
      { responseType: 'blob' },
    );
    const blob = response.data;

    if (blob && typeof blob.type === 'string' && blob.type.includes('application/json')) {
      const payload = JSON.parse(await blob.text());
      if (payload?.download_url) {
        return { downloadUrl: payload.download_url };
      }
      throw new Error(payload?.detail || 'Download failed.');
    }
    return { blob };
  }
}

const apiService = new ApiService();
export default apiService;